import React, { useEffect } from "react";
import { Col, Form, Input, Row, Typography } from "antd";
import dayjs from "dayjs";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { convertUtcToIst } from "../../../../helpers/utility";

const { Text } = Typography;

/**
 * Unlike the other forms, which print the vitals chart read-only, the
 * emergency shift form is filled at the bedside: the most recent charted
 * reading is used as a starting point and every box stays editable.
 */
export const VITAL_FIELDS = [
  { name: "temperature", label: "Temperature", unit: "°F" },
  { name: "pulse", label: "Pulse Rate", unit: "bpm" },
  { name: "respiration", label: "Respiratory Rate", unit: "/min" },
  { name: "oxygen_saturation", label: "SPO2", unit: "%" },
  { name: "systolic_pressure", label: "Systolic Pressure", unit: "mmHg" },
  { name: "diastolic_pressure", label: "Diastolic Pressure", unit: "mmHg" },
];

/* GRBS and insulin come off the sugar chart rather than a vitals round, so
   they are asked for on their own and submitted under their own `grbs` key
   rather than buried in the vitals reading. */
export const GRBS_FIELDS = [
  { name: "grbs", label: "GRBS", unit: "mg/dL" },
  { name: "insulin", label: "Insulin", unit: "units" },
];

/** Which object on the form each group of fields is stored under. */
export const VITALS_KEY = "vitals";
export const GRBS_KEY = "grbs";

/** Most recent charted reading, by action_time rather than list order. */
export const getLatestVitalsRecord = (vitals) => {
  if (!Array.isArray(vitals) || !vitals.length) return null;
  return [...vitals].sort(
    (a, b) => dayjs(b?.action_time).valueOf() - dayjs(a?.action_time).valueOf()
  )[0];
};

const EmergencyVitals = ({ form, details }) => {
  const latest = getLatestVitalsRecord(details?.data?.vitals);
  /* The sugar chart is its own list; the vitals row carries the same two
     columns, so it stands in when nothing has been charted separately. */
  const latestGrbs =
    getLatestVitalsRecord(details?.data?.grbs) || latest || null;

  // Seed from the last charted reading, leaving anything already typed (or
  // restored from a saved form) untouched.
  useEffect(() => {
    if (!form) return;
    const seed = (key, fields, source) => {
      if (!source) return;
      const current = form.getFieldValue(key) || {};
      const seeded = { ...current };
      let changed = false;
      fields.forEach(({ name }) => {
        const existing = current[name];
        const value = source[name];
        if (
          (existing === undefined || existing === null || existing === "") &&
          value !== undefined &&
          value !== null &&
          value !== ""
        ) {
          seeded[name] = String(value);
          changed = true;
        }
      });
      if (key === VITALS_KEY && !seeded.recorded_at && source?.action_time) {
        seeded.recorded_at = source.action_time;
        changed = true;
      }
      if (changed) form.setFieldValue(key, seeded);
    };
    seed(VITALS_KEY, VITAL_FIELDS, latest);
    seed(GRBS_KEY, GRBS_FIELDS, latestGrbs);
  }, [form, latest, latestGrbs]);

  const renderField = (groupKey) => (field) => (
    <Col xs={24} sm={12} md={8} lg={6} key={field.name}>
      <Form.Item label={field.label} name={[groupKey, field.name]}>
        <Input
          className={inputStyles.mdInput}
          placeholder={field.unit}
          suffix={
            <Text type="secondary" className="emergency-vitals-unit">
              {field.unit}
            </Text>
          }
        />
      </Form.Item>
    </Col>
  );

  return (
    <>
      <Text type="secondary" className="emergency-section-hint">
        {latest?.action_time
          ? `Prefilled from the reading charted on ${convertUtcToIst(
              latest.action_time,
              true,
              true
            )}. Edit any box to record what was observed now.`
          : "No charted vitals yet — enter what was observed."}
      </Text>
      <Row gutter={16} className="emergency-vitals-grid">
        {VITAL_FIELDS.map(renderField(VITALS_KEY))}
      </Row>

      <Text strong className="forms-ip-subsection-title">
        GRBS
      </Text>
      <Row gutter={16} className="emergency-vitals-grid">
        {GRBS_FIELDS.map(renderField(GRBS_KEY))}
      </Row>
    </>
  );
};

export default EmergencyVitals;
